import { LocationMarkerIcon } from "@heroicons/react/outline";
import { isEmpty, orderBy, values } from "lodash";
import moment from "moment";
import React, { useCallback, useMemo, useState } from "react";
import { Link, RouteComponentProps, useLocation } from "react-router-dom";
import AlertMessage from "../../../../components/AlertMessage";
import DeleteLocationButton from "../../../../components/DeleteLocationButton";
import DisplayLabels from "../../../../components/labels/DisplayLabels";
import { useLocations } from "../../../../context/locations";
import { useTransmitScales } from "../../../../context/transmitScales";
import { ClassificationValue } from "../../../../utils/hotspotUtils";
import { h3ToParentMemoized } from "../../../../utils/memoizedMethods";
import useTransmitScaleConfig from "../../../../utils/useTransmitScaleConfig";
import TablePagination from "../../../Workspace/common/TablePagination";
import usePagination, {
  PaginationKey
} from "../../../Workspace/common/usePagination";

const LocationsInHexTab: React.FC<RouteComponentProps<{ h3Index: string }>> = (
  props
) => {
  const [showDescription, setShowDescription] = useState(true);
  const {
    getHexTransmitScaleInfo,
    calculatedTransmitScales,
    isTransmitScaleActive
  } = useTransmitScales();
  const toggleIsActive = useTransmitScaleConfig(
    (state) => state.toggleIsActive
  );
  const { locations } = useLocations();
  const { search: queryParamsString } = useLocation();
  const { resolution } = getHexTransmitScaleInfo(props.match.params.h3Index);

  const locationsInHex = useMemo(
    () =>
      values(locations)
        .filter(
          ({ h3_index_r12 }) =>
            h3ToParentMemoized(h3_index_r12, resolution) ===
            props.match.params.h3Index
        )
        .map((location) => ({
          location,
          transmitScale: calculatedTransmitScales[location.uuid] || 0,
          isActive: isTransmitScaleActive(location.uuid)
        })),
    [
      calculatedTransmitScales,
      isTransmitScaleActive,
      locations,
      props.match.params.h3Index,
      resolution
    ]
  );

  const { startIndex, endIndex } = usePagination(
    useCallback(
      (state) =>
        state.getPaginationInfo(
          PaginationKey.hexLocations,
          locationsInHex.length,
          queryParamsString
        ),
      [locationsInHex.length, queryParamsString]
    )
  );

  const renderInfoBox = useMemo(
    () => (
      <AlertMessage
        className="mb-0"
        type="info"
        title={
          <>
            <p>
              Add{" "}
              <Link
                className="inline-flex hover:text-hotspotty-500"
                to="/locations"
              >
                locations
              </Link>{" "}
              to plan where your hotspots can be installed and simulate the
              impact on reward scaling.
            </p>
          </>
        }
        onClose={() => setShowDescription(false)}
      />
    ),
    []
  );

  if (isEmpty(locationsInHex)) {
    return showDescription ? (
      renderInfoBox
    ) : (
      <p className="mt-2 text-sm text-gray-500 dark:text-gray-50">
        There are no locations in this hex so far.
      </p>
    );
  }

  return (
    <>
      {showDescription && renderInfoBox}
      <div className="mt-6 flex flex-col">
        <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="py-2 align-middle inline-block min-w-full sm:px-6 lg:px-8">
            <div className="shadow overflow-hidden border-b border-gray-200 dark:border-gray-500 sm:rounded-lg">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-500">
                <thead className="bg-gray-50 text-gray-500 dark:bg-gray-700 dark:text-gray-300">
                  <tr>
                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      {locationsInHex.length} location
                      {locationsInHex.length > 1 ? "s" : ""}
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Added to hex
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Transmit scale
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Activated
                    </th>

                    <th
                      scope="col"
                      className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider"
                    >
                      Delete
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 text-gray-500 dark:text-gray-50 dark:divide-gray-500">
                  {orderBy(locationsInHex, "location.created_at", "desc")
                    .splice(startIndex, endIndex)
                    .map(({ location, transmitScale, isActive }) => (
                      <tr key={location.uuid}>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Link
                            className="relative flex items-center space-x-3 group"
                            to={`/locations/${location.uuid}/reward-scaling`}
                          >
                            <div className="flex-shrink-0">
                              <span className="inline-block relative">
                                <LocationMarkerIcon className="h-10 w-10" />
                              </span>
                            </div>
                            <div className="min-w-0 focus:outline-none">
                              <p className="text-sm font-medium text-gray-900 dark:text-white group-hover:underline">
                                {location.name}
                              </p>
                              <DisplayLabels
                                linkedUuid={location.uuid}
                                size="small"
                              />
                            </div>
                          </Link>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {location.created_at
                            ? moment(location.created_at).utc().fromNow()
                            : "-"}
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <ClassificationValue value={transmitScale} />
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <button
                              type="button"
                              className={`relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none ${
                                isActive
                                  ? "bg-hotspotty-400 dark:bg-gray-100 dark:hover:bg-gray-200"
                                  : "bg-gray-200 dark:bg-gray-500 dark:hover:bg-gray-400"
                              }`}
                              aria-pressed="false"
                              aria-labelledby="toggle-interactive"
                              onClick={() => toggleIsActive(location.uuid)}
                            >
                              <span
                                aria-hidden="true"
                                className={`pointer-events-none inline-block h-5 w-5 rounded-full bg-white dark:bg-gray-800 shadow transform ring-0 transition ease-in-out duration-200 ${
                                  isActive ? "translate-x-5" : "translate-x-0"
                                }`}
                              />
                            </button>
                          </div>
                        </td>

                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <DeleteLocationButton id={location.uuid} />
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
              <TablePagination
                paginationKey={PaginationKey.hexLocations}
                itemsCount={locationsInHex.length}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default LocationsInHexTab;
